import clsx from 'clsx';

/**
 * A step's icon sits in a box exactly one label line tall, and the row aligns both to its top, so
 * the icon is centred on the first line of the label however the label wraps and whatever glyph
 * the status draws. The image the running status draws is a block, so no inline descender space
 * pushes it off that centre.
 */
export const TASK_ROW_CLASSES = 'flex items-start gap-xs py-2xs';
export const TASK_ICON_CLASSES =
  'relative z-1 flex size-5 shrink-0 items-center justify-center [&_img]:block';
export const TASK_LABEL_CLASSES = 'text-sm leading-5 text-foreground';

/**
 * The tree connectors are pseudo-elements rather than real nodes so the rows stay flat in the a11y
 * tree: `::before` is the vertical trunk, `::after` the elbow that joins a nested row to it.
 *
 * Their offsets follow from the row above: a 20px icon box (`size-5`) under a 4px inset (`py-2xs`)
 * puts every icon's centre 10px in and 14px down, and its box's bottom 24px down. A nested row is
 * indented by one icon box (`ml-5`), so its parent's trunk runs 10px to its left. Changing the
 * icon box or the row inset means moving these with it.
 */
export const getTaskItemClasses = (level: number, isLastInGroup: boolean) => {
  const isRoot = level === 0;

  // A root row continues the trunk from under its icon to the next root's icon. A nested row draws
  // its stretch of the parent's trunk, from the parent's icon (the row inset above it) down to the
  // next sibling, or to its own elbow when it is the last.
  return clsx(
    "relative before:absolute before:w-px before:-translate-x-1/2 before:bg-border before:content-['']",
    isRoot && 'before:left-2.5 before:top-6 before:-bottom-1',
    isRoot && isLastInGroup && 'before:hidden',
    !isRoot && 'ml-5 before:-left-2.5 before:-top-1',
    !isRoot && (isLastInGroup ? 'before:h-4.5' : 'before:bottom-0'),
    !isRoot &&
      "after:absolute after:-left-2.5 after:top-3.5 after:h-px after:w-2.5 after:-translate-y-1/2 after:bg-border after:content-['']",
  );
};
